/**
 * Phase 6 — Executive Report Archive
 * Permission reuse: EXEC_REPORT_EXPORT.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download, RotateCw, FileText } from "lucide-react";

export default function ExecutiveReports() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState("");
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [working, setWorking] = useState(false);

  const load = () => {
    setErr(null);
    const q = new URLSearchParams();
    if (year) q.set("year", year);
    if (month) q.set("month", month);
    api.get(`/executive/reports?${q.toString()}`)
      .then(({ data }) => setRows(data.items || []))
      .catch((e) => {
        if (e?.response?.status === 403) setErr("Tidak berwenang mengakses arsip laporan.");
        else setErr(formatApiError(e));
      });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const regenerate = async () => {
    if (!year || !month) {
      toast.error("Isi Tahun dan Bulan terlebih dahulu untuk membuat laporan baru.");
      return;
    }
    setWorking(true);
    try {
      await api.post(`/executive/reports/generate?year=${year}&month=${month}`);
      toast.success(`Laporan ${year}-${month} berhasil dibuat.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setWorking(false); }
  };

  const download = async (row) => {
    try {
      const res = await api.get(`/executive/reports/${row.report_id}/download`,
                                   { responseType: "blob" });
      const blob = new Blob([res.data], { type: row.mime_type || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = row.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (err) {
    return (
      <div className="p-6 space-y-4" data-testid="exec-reports-page">
        <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
          <ArrowLeft size={12} className="mr-1" />Kembali
        </Link>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="exec-reports-no-access">
          {err}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="exec-reports-page">
      <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
        <ArrowLeft size={12} className="mr-1" />Kembali ke Dashboard Eksekutif
      </Link>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Arsip Laporan Eksekutif</h1>
          <p className="text-xs md:text-sm text-slate-500">
            Riwayat PDF Executive Management Report dengan versioning per periode. Laporan tidak di-overwrite — setiap regenerate menambah versi baru.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-xs">Tahun</Label>
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || "")} className="w-24" data-testid="arch-year" />
          <Label className="text-xs">Bulan</Label>
          <Input type="number" min="1" max="12" value={month} onChange={(e) => setMonth(e.target.value ? Math.max(1, Math.min(12, Number(e.target.value))) : "")} className="w-20" data-testid="arch-month" placeholder="opsional" />
          <Button variant="outline" size="sm" onClick={regenerate} disabled={working || !year || !month} data-testid="arch-regenerate">
            <RotateCw size={14} className={`mr-1 ${working ? "animate-spin" : ""}`} />Regenerate Versi Baru
          </Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Riwayat Laporan</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Periode</TableHead>
              <TableHead>Versi</TableHead>
              <TableHead>File</TableHead>
              <TableHead>Ukuran</TableHead>
              <TableHead>Dibuat</TableHead>
              <TableHead>Oleh</TableHead>
              <TableHead>Checksum</TableHead>
              <TableHead>HR</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-xs text-slate-400 italic py-6">
                  Belum ada laporan tersimpan. Isi Tahun+Bulan lalu klik Regenerate untuk membuat laporan pertama.
                </TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.report_id} data-testid={`arch-row-${r.report_id}`}>
                  <TableCell>{r.year}-{String(r.month).padStart(2, "0")}</TableCell>
                  <TableCell><Badge variant="outline">v{r.version}</Badge></TableCell>
                  <TableCell className="text-xs font-mono">{r.file_name}</TableCell>
                  <TableCell className="text-xs">{(r.file_size / 1024).toFixed(1)} KB</TableCell>
                  <TableCell className="text-xs">{new Date(r.generated_at).toLocaleString("id-ID")}</TableCell>
                  <TableCell className="text-xs">{r.generated_by}<div className="text-[10px] text-slate-500">{r.generated_by_role}</div></TableCell>
                  <TableCell className="text-xs font-mono text-slate-500">{(r.checksum || "").slice(0, 10)}…</TableCell>
                  <TableCell>
                    {r.has_hr_full ? <Badge className="bg-amber-100 text-amber-900 text-[10px]">HR</Badge>
                                       : <Badge variant="outline" className="text-[10px]">masked</Badge>}
                  </TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm" onClick={() => download(r)}
                              data-testid={`arch-dl-${r.report_id}`}>
                      <Download size={12} className="mr-1" />Unduh
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
